import React from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Paper
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { getFileCategories } from './FileTypeIcon';

const SearchFilters = ({
  searchTerm,
  onSearchChange,
  categoryFilter,
  onCategoryChange,
  sortBy,
  onSortChange,
  sortOrder,
  onSortOrderChange,
  onClearFilters,
  totalDocuments = 0,
  filteredDocuments = 0
}) => {
  const fileCategories = getFileCategories();

  const sortOptions = [
    { value: 'name', label: 'File Name' },
    { value: 'lastModified', label: 'Date Modified' },
    { value: 'size', label: 'File Size' }
  ];

  const hasActiveFilters = searchTerm || categoryFilter !== 'all' || sortBy !== 'name' || sortOrder !== 'asc';

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: { xs: 'stretch', md: 'center' }
        }}
      >
        {/* Search Field */}
        <TextField
          placeholder="Search documents..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          size="small"
          sx={{ 
            flex: 1,
            minWidth: { xs: '100%', md: 250 }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => onSearchChange('')}
                  sx={{ p: 0.5 }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        {/* Category Filter */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={categoryFilter}
            onChange={(e) => onCategoryChange(e.target.value)}
            label="Category"
          >
            <MenuItem value="all">All Types</MenuItem>
            {fileCategories.map((category) => (
              <MenuItem key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Sort By */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value)}
            label="Sort By"
          >
            {sortOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Sort Order */}
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Order</InputLabel>
          <Select
            value={sortOrder}
            onChange={(e) => onSortOrderChange(e.target.value)}
            label="Order"
          >
            <MenuItem value="asc">A-Z / Low-High</MenuItem>
            <MenuItem value="desc">Z-A / High-Low</MenuItem>
          </Select>
        </FormControl>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <Tooltip title="Clear all filters">
            <IconButton
              onClick={onClearFilters}
              color="primary"
              sx={{
                bgcolor: 'primary.light',
                '&:hover': { bgcolor: 'primary.main', color: 'white' }
              }}
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Filter Summary */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 1,
          mt: 2
        }}
      >
        {/* Active Filters */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {searchTerm && (
            <Chip
              label={`Search: "${searchTerm}"`}
              size="small"
              onDelete={() => onSearchChange('')}
              color="primary"
              variant="outlined"
            />
          )}
          {categoryFilter !== 'all' && (
            <Chip
              label={`Type: ${categoryFilter.charAt(0).toUpperCase() + categoryFilter.slice(1)}`}
              size="small"
              onDelete={() => onCategoryChange('all')}
              color="primary"
              variant="outlined"
            />
          )}
          {(sortBy !== 'name' || sortOrder !== 'asc') && (
            <Chip
              label={`Sort: ${sortOptions.find(opt => opt.value === sortBy)?.label} (${sortOrder === 'asc' ? 'A-Z' : 'Z-A'})`}
              size="small"
              onDelete={() => {
                onSortChange('name');
                onSortOrderChange('asc');
              }}
              color="primary"
              variant="outlined"
            />
          )}
        </Box>

        {/* Results Count */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'text.secondary',
            fontSize: '0.875rem'
          }}
        >
          <FilterIcon fontSize="small" />
          {filteredDocuments !== totalDocuments
            ? `Showing ${filteredDocuments} of ${totalDocuments} documents`
            : `${totalDocuments} documents`
          }
        </Box>
      </Box>
    </Paper>
  );
};

export default SearchFilters;